import type { Faq } from '../components/FaqList'

export type CategoryContent = {
  /** short noun used when composing service-level copy */
  systemNoun: string
  valueStatement: string
  overview: string[]
  symptoms: string[]
  faqs: Faq[]
}

export const categoryContent: Record<string, CategoryContent> = {
  'diagnostics-and-inspections': {
    systemNoun: 'diagnostic and inspection work',
    valueStatement:
      'Testing and inspection that identify what is actually happening with your vehicle before any parts are recommended.',
    overview: [
      'Diagnostics is where most repairs should start. A warning light, a noise, a leak or a no-start can each have several possible causes, and replacing parts without testing is how customers end up paying twice.',
      'East End uses modern diagnostic equipment to test the system in question, then explains the findings in plain language so you can decide what to do next.',
    ],
    symptoms: [
      'A check engine light or other warning light is on',
      'The vehicle does not start, or starts inconsistently',
      'A noise, vibration or smell that was not there before',
      'A fluid spot under the vehicle you cannot identify',
      'The vehicle drives differently than it used to',
    ],
    faqs: [
      {
        question: 'Why does diagnosis take testing instead of a quick look?',
        answer:
          'A single symptom can be caused by several different components. Testing narrows it down so the repair recommendation is based on confirmed information rather than a guess.',
      },
      {
        question: 'Will you tell me what you found before doing repairs?',
        answer:
          'Yes. We explain the condition, the cause we confirmed, and the correction we recommend so you can decide what happens next.',
      },
      {
        question: 'Can you look at a used vehicle before I buy it?',
        answer:
          'Pre-purchase inspections are part of this category. Call the shop to talk through the vehicle and what you want inspected.',
      },
    ],
  },
  'preventive-maintenance': {
    systemNoun: 'routine maintenance',
    valueStatement:
      'Routine service that keeps your vehicle on its maintenance schedule and catches small issues while they are still small.',
    overview: [
      'Preventive maintenance is the least expensive work a vehicle needs. Fluids, filters, belts, hoses and batteries all have service intervals, and staying close to them keeps you out of the repair bay.',
      'When you bring a vehicle in for maintenance, we will tell you what is due now and what can reasonably wait, so you can plan instead of react.',
    ],
    symptoms: [
      'You are past due or unsure when the last service was performed',
      'A maintenance reminder or oil life indicator is on',
      'Reduced airflow from the vents or a musty cabin smell',
      'Rough running, hesitation or a drop in fuel economy',
      'Slow cranking, or a battery that has needed a jump start',
    ],
    faqs: [
      {
        question: 'How do I know what maintenance my vehicle is due for?',
        answer:
          'Bring the vehicle in or call the shop with the year, make, model and mileage. We can review the factory schedule and tell you what applies.',
      },
      {
        question: 'Do you tell me everything at once?',
        answer:
          'We tell you what we found and separate what needs attention now from what can wait, so maintenance can be spread out sensibly.',
      },
      {
        question: 'Is maintenance covered by a warranty?',
        answer:
          'Repairs we perform carry a 1 year / 12,000 mile parts and labor warranty. Call the shop with questions about your specific service.',
      },
    ],
  },
  'brake-services': {
    systemNoun: 'brake work',
    valueStatement:
      'Inspection, service and repair for how your vehicle stops — from pads and rotors to hydraulics and ABS.',
    overview: [
      'Braking involves friction parts, hydraulics and electronics working together. Noise, vibration or a change in pedal feel can each point to a different part of that system.',
      'We inspect the brake system, confirm what is causing the symptom, and explain what needs replacing now versus what still has service life left.',
    ],
    symptoms: [
      'Squeaking, squealing or grinding when braking',
      'Vibration or pulsation felt through the pedal or steering wheel',
      'A brake pedal that feels soft, low or spongy',
      'A brake warning light or ABS light on the dash',
      'The vehicle pulls to one side while braking, or takes longer to stop',
    ],
    faqs: [
      {
        question: 'Do I need rotors every time I need pads?',
        answer:
          'Not always. Rotor condition is measured and inspected during brake service, and we explain what we found before recommending replacement.',
      },
      {
        question: 'Is the brake inspection free?',
        answer:
          'A free brake inspection is included with the purchase of brakes. Call the shop to have the current offer applied to your visit.',
      },
      {
        question: 'Should I keep driving if my brakes are making noise?',
        answer:
          'Brake noise may indicate normal wear or something more involved. Because braking is a safety system, it is worth calling the shop and having it inspected rather than waiting.',
      },
    ],
  },
  'steering-and-suspension': {
    systemNoun: 'steering and suspension work',
    valueStatement:
      'Repairs for how your vehicle rides, steers and wears its tires — shocks, struts, joints, bearings and steering components.',
    overview: [
      'Steering and suspension components wear gradually, so the change is easy to miss until tire wear, noise or a loose feeling in the wheel makes it obvious.',
      'We inspect the related components together, because a clunk or a pull can come from more than one part of the system.',
    ],
    symptoms: [
      'Clunking or knocking over bumps',
      'Uneven or accelerated tire wear',
      'The steering wheel feels loose, heavy or off-center',
      'The vehicle wanders, pulls or feels unsettled at highway speed',
      'A humming or growling noise that changes with speed or cornering',
    ],
    faqs: [
      {
        question: 'Why does my vehicle wear tires unevenly?',
        answer:
          'Uneven wear can be related to alignment, worn suspension components or tire pressure. An inspection identifies which of those is involved before parts are recommended.',
      },
      {
        question: 'Do shocks and struts have to be replaced in pairs?',
        answer:
          'Ride and handling components are inspected as a system, and we explain what we found on each corner before recommending what to replace.',
      },
      {
        question: 'Can you check a noise I only hear over bumps?',
        answer:
          'Yes. Noise and vibration concerns are diagnosed by testing and inspection so the cause is confirmed rather than assumed.',
      },
    ],
  },
  'tires-and-wheels': {
    systemNoun: 'tire and wheel service',
    valueStatement:
      'Tire service, balancing and inspection that keeps your tires wearing evenly and your ride smooth.',
    overview: [
      'Tires are the only part of the vehicle touching the road, and they show the effects of pressure, alignment and suspension condition.',
      'We inspect tire and wheel condition, service what needs attention, and point out anything in the related systems that is affecting how the tires wear.',
    ],
    symptoms: [
      'A tire that keeps losing air',
      'A vibration that appears or worsens at certain speeds',
      'A tire pressure warning light on the dash',
      'Visible uneven wear across the tread',
      'A puncture or damage you noticed while checking the tires',
    ],
    faqs: [
      {
        question: 'Why is my TPMS light on when the tires look fine?',
        answer:
          'A TPMS light can be related to low pressure, temperature change or a sensor issue. Testing identifies which one applies.',
      },
      {
        question: 'Can a tire be repaired instead of replaced?',
        answer:
          'It depends on the location and extent of the damage. We inspect the tire and explain whether repair is appropriate.',
      },
      {
        question: 'How often should tires be rotated?',
        answer:
          'Rotation intervals vary by vehicle and tire. Call the shop with your year, make, model and mileage and we can go over what applies.',
      },
    ],
  },
  'heating-and-air-conditioning': {
    systemNoun: 'heating and A/C work',
    valueStatement:
      'Automotive heating and air conditioning diagnosis, testing and repair — including both R-134a and R-1234yf systems.',
    overview: [
      'An A/C system that is not cooling can be down to pressure, a leak, an electrical fault or a failed component. Adding refrigerant without testing does not answer which one it is.',
      'We test the system, measure how it is actually performing, and explain the findings before recommending repair.',
    ],
    symptoms: [
      'The A/C is not blowing cold, or only cools sometimes',
      'Weak airflow from the vents',
      'Unusual noise when the A/C is switched on',
      'An odor coming from the vents',
      'Signs of refrigerant leakage, or a compressor that does not engage',
    ],
    faqs: [
      {
        question: 'What does the $29.99 A/C pressure test include?',
        answer:
          'It covers a comprehensive A/C system inspection, refrigerant level verification, a compressor performance test and an interior vent temperature test, plus tax and shop fees. Refrigerant, parts and repairs are not included in that price.',
      },
      {
        question: 'Do you service both older and newer refrigerant systems?',
        answer:
          'Yes. We service R-134a systems and the newer R-1234yf systems used on later-model vehicles.',
      },
      {
        question: 'Can you just add refrigerant?',
        answer:
          'Low refrigerant usually means it escaped somewhere. Testing first tells us whether there is a leak to address rather than repeating the same recharge later.',
      },
    ],
  },
  'engine-repair': {
    systemNoun: 'engine work',
    valueStatement:
      'Engine diagnostics, performance repair and gasket work handled by factory-trained technicians.',
    overview: [
      'Engine concerns cover a wide range — a misfire, a leak, a noise or a performance complaint each require different testing before a repair path makes sense.',
      'We confirm the cause first, explain the options and the tradeoffs, and let you decide how to proceed.',
    ],
    symptoms: [
      'A rough idle, misfire or hesitation under acceleration',
      'A check engine light related to engine performance',
      'Oil spots under the vehicle or a burning oil smell',
      'A noticeable drop in power or fuel economy',
      'A new ticking, knocking or rattling noise from the engine',
    ],
    faqs: [
      {
        question: 'Is a check engine light always an engine problem?',
        answer:
          'No. The light can be triggered by several systems. Diagnostics identify which system and which component is involved.',
      },
      {
        question: 'Do you explain repair options before starting work?',
        answer:
          'Yes. Once the cause is confirmed we go over the correction and the options so you can decide what happens next.',
      },
      {
        question: 'Are engine repairs covered by a warranty?',
        answer:
          'Repairs we perform are covered by a 1 year / 12,000 mile parts and labor warranty.',
      },
    ],
  },
  'cooling-system': {
    systemNoun: 'cooling system work',
    valueStatement:
      'Overheating, coolant leaks and cooling system service traced back to the actual source.',
    overview: [
      'The cooling system keeps engine temperature in range. A leak, a failed thermostat, a water pump or a fan fault can each show up as overheating or coolant loss.',
      'Pressure testing and inspection identify where coolant is going and what is driving the temperature problem before parts are replaced.',
    ],
    symptoms: [
      'The temperature gauge reads higher than normal, or the vehicle overheats',
      'Coolant spots under the vehicle or a sweet smell after driving',
      'The coolant reservoir keeps needing to be topped off',
      'Heat from the vents is weak or inconsistent',
      'Steam or a hot smell from under the hood',
    ],
    faqs: [
      {
        question: 'Why does my coolant keep disappearing?',
        answer:
          'Coolant loss usually means it is escaping somewhere. A pressure test helps locate where before anything is replaced.',
      },
      {
        question: 'Should I keep driving an overheating vehicle?',
        answer:
          'Continuing to drive while overheating can lead to further damage. Call the shop and we will talk through the situation.',
      },
      {
        question: 'How often should coolant be serviced?',
        answer:
          'Coolant service intervals vary by vehicle. Call with your year, make, model and mileage and we can review what applies.',
      },
    ],
  },
  'electrical-system': {
    systemNoun: 'electrical work',
    valueStatement:
      'Batteries, charging, starting, lighting and the intermittent electrical faults that are hard to pin down.',
    overview: [
      'Electrical concerns are often traced rather than guessed. A no-start can be a battery, a starter, a charging fault, or a connection problem, and each tests differently.',
      'We test the circuit and components involved, then explain what the testing showed before recommending a repair.',
    ],
    symptoms: [
      'The vehicle cranks slowly or will not start',
      'A battery or charging warning light on the dash',
      'Lights that dim, flicker or stop working',
      'Power windows or door locks that work inconsistently',
      'A repeated blown fuse or an accessory that stopped working',
    ],
    faqs: [
      {
        question: 'Is a no-start always the battery?',
        answer:
          'No. Batteries, starters, charging components and connections all produce similar symptoms. Testing identifies which one is at fault.',
      },
      {
        question: 'Can you find an intermittent electrical problem?',
        answer:
          'Intermittent faults take testing and time. Call the shop and describe when it happens so we know what to look for.',
      },
      {
        question: 'Do you test the battery before replacing it?',
        answer:
          'Yes. Battery and charging system testing is part of how we confirm what is actually causing the symptom.',
      },
    ],
  },
  'fuel-and-ignition-systems': {
    systemNoun: 'fuel and ignition work',
    valueStatement:
      'Fuel delivery and ignition diagnosis and repair for hard starts, misfires and rough running.',
    overview: [
      'Fuel and ignition problems tend to show up as a running complaint — hard starting, a misfire, hesitation or a loss of power.',
      'Testing fuel pressure and ignition components identifies which side of the system is involved before parts are replaced.',
    ],
    symptoms: [
      'Hard starting or extended cranking',
      'A misfire, stumble or rough idle',
      'Hesitation or a lack of power under acceleration',
      'A check engine light with a running complaint',
      'A noticeable drop in fuel economy',
    ],
    faqs: [
      {
        question: 'Will new spark plugs fix a misfire?',
        answer:
          'Sometimes, but a misfire can also involve ignition coils, fuel delivery or a mechanical issue. Testing identifies which before parts are replaced.',
      },
      {
        question: 'Can you test fuel pressure?',
        answer:
          'Yes. Fuel pressure testing is part of how we evaluate fuel delivery concerns.',
      },
      {
        question: 'What should I have ready when I call?',
        answer:
          'The year, make, model, and a description of when the symptom happens — cold start, idle, acceleration — helps us know what to test.',
      },
    ],
  },
  'transmission-and-drivetrain': {
    systemNoun: 'transmission and drivetrain work',
    valueStatement:
      'Shifting concerns, axles, and drivetrain repair explained clearly before any work begins.',
    overview: [
      'Transmission and drivetrain concerns cover shifting quality, noise, vibration and fluid condition. The right next step depends on what testing shows.',
      'Because this work can be significant, we make a point of explaining the findings and the options so you can weigh them yourself.',
    ],
    symptoms: [
      'Delayed, harsh or slipping shifts',
      'A clunk when shifting into gear',
      'A vibration that changes with speed',
      'A clicking noise when turning',
      'A leak or low fluid found under the vehicle',
    ],
    faqs: [
      {
        question: 'Do you diagnose before recommending transmission work?',
        answer:
          'Yes. Transmission and drivetrain diagnostics come first so the recommendation reflects what was actually found.',
      },
      {
        question: 'Is financing available for larger repairs?',
        answer:
          'Financing is available through Snap Finance, Affirm, Klarna and Sunbit. Ask us about it when a repair is larger than expected.',
      },
      {
        question: 'Can you service transmission fluid?',
        answer:
          'Transmission fluid service is part of this category. Call with your year, make, model and mileage and we can go over what applies.',
      },
    ],
  },
  'exhaust-and-emissions': {
    systemNoun: 'exhaust and emissions work',
    valueStatement:
      'Exhaust noise, leaks and emissions-related faults inspected and repaired from the manifold back.',
    overview: [
      'Exhaust concerns are often noise related, while emissions concerns usually show up as a warning light and a stored fault.',
      'We inspect the exhaust system and test emissions-related components so the repair addresses the actual fault rather than the symptom.',
    ],
    symptoms: [
      'A louder than normal exhaust, or a new rattle underneath',
      'A check engine light related to emissions',
      'A fuel or exhaust smell around the vehicle',
      'Reduced power or a drop in fuel economy',
      'A visible hole, rust-through or hanging exhaust component',
    ],
    faqs: [
      {
        question: 'Does a check engine light mean the catalytic converter?',
        answer:
          'Not necessarily. Emissions faults can involve sensors, the EVAP system or engine performance. Diagnostics identify the actual cause.',
      },
      {
        question: 'Can an exhaust leak be repaired without full replacement?',
        answer:
          'It depends on the location and condition of the component. We inspect it and explain what we found before recommending a repair.',
      },
      {
        question: 'Why does my gas cap warning relate to emissions?',
        answer:
          'The EVAP system is monitored for leaks, and small leaks can set a fault. Testing pinpoints where the leak is.',
      },
    ],
  },
  'additional-services': {
    systemNoun: 'general repair and inspection work',
    valueStatement:
      'The everyday repairs, replacements and inspections that keep a vehicle or a small fleet dependable.',
    overview: [
      'Not everything fits neatly into one system. Lighting, belts, wipers, inspections and maintenance planning all fall into this category.',
      'If you are not sure what your vehicle needs, call the shop and describe what is going on — we will tell you what to look at first.',
    ],
    symptoms: [
      'A bulb or exterior light is out',
      'A squeal from the belt area, especially on start-up',
      'Wipers that streak, chatter or skip',
      'A long trip coming up and you want the vehicle looked over',
      'Several vehicles to keep on the road for a small business',
    ],
    faqs: [
      {
        question: 'Do you work on fleet vehicles?',
        answer:
          'Fleet vehicle maintenance and repair is part of this category. Call the shop to talk through what your vehicles need.',
      },
      {
        question: 'Can you look over a vehicle before a road trip?',
        answer:
          'Yes. A pre-trip inspection is a good way to look at the items that matter before a long drive.',
      },
      {
        question: 'What if I do not know what service I need?',
        answer:
          'Call and describe what the vehicle is doing. Diagnosis-first is how every visit works here.',
      },
    ],
  },
}
